import React, { useState, useEffect } from "react";
import { Snackbar, Alert } from "@mui/material";
import { getCurrentUser } from "../services/pocketbase";

const CSSLabLeak = () => {
  const [active, setActive] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [roleAssigned, setRoleAssigned] = useState(false);
  const [roleError, setRoleError] = useState(null);
  const user = getCurrentUser();

  useEffect(() => {
    // Handler for keyboard shortcut (Ctrl+Alt+C)
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.altKey && event.key.toLowerCase() === "c") {
        event.preventDefault();
        setActive((prev) => !prev);
        setShowWarning(true);

        // Only attempt to assign role if we have a Discord ID and role hasn't been assigned yet
        if (user?.discord_id && !roleAssigned) {
          assignDiscordRole();
        }

        // Hide warning after 5 seconds
        setTimeout(() => setShowWarning(false), 5000);
      }
    };

    // Add event listener
    window.addEventListener("keydown", handleKeyDown);

    // Apply or remove effects when active state changes
    if (active) {
      applyCSSLabLeakEffects();
    } else {
      removeCSSLabLeakEffects();
    }

    // Clean up
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      removeCSSLabLeakEffects();
    }; // eslint-disable-next-line
  }, [active, roleAssigned, user]);

  // Function to assign Discord role
  const assignDiscordRole = async () => {
    try {
      setRoleError(null);

      const response = await fetch("/api/discord/assign-role", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          discordId: user.discord_id,
          roleId: "1374801324201213952", // CSS Lab Leak role ID
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.details || data.error || "Failed to assign role");
      }

      setRoleAssigned(true);
    } catch (err) {
      console.error("Error assigning CSS Lab Leak role:", err);
      setRoleError(err.message || "Failed to assign Discord role");
    }
  };

  // Apply the CSS Lab Leak effects
  const applyCSSLabLeakEffects = () => {
    // Create style element for our mutations
    const style = document.createElement("style");
    style.id = "css-lab-leak-style";
    style.innerHTML = `
      /* Buttons grow fur */
      button:hover, .MuiButton-root:hover {
        position: relative;
        z-index: 1000;
        filter: url('data:image/svg+xml;charset=utf8,<svg xmlns="http://www.w3.org/2000/svg"><filter id="filter"><feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="5" /><feDisplacementMap xChannelSelector="R" yChannelSelector="G" scale="20" in="SourceGraphic" /></filter></svg>#filter');
        transform: scale(1.05);
        transition: all 0.3s ease;
        box-shadow: 0 0 10px rgba(255, 0, 255, 0.7);
      }
      
      /* Text becomes comic sans then wingdings */
      p:hover, span:hover, h1:hover, h2:hover, h3:hover, h4:hover, h5:hover, h6:hover, a:hover, label:hover, .MuiTypography-root:hover {
        font-family: "Comic Sans MS", cursive, sans-serif !important;
        animation: fontChange 2s infinite alternate;
        color: #ff00ff !important;
        text-shadow: 0 0 5px rgba(0, 255, 255, 0.7) !important;
      }
      
      @keyframes fontChange {
        0% { font-family: "Comic Sans MS", cursive, sans-serif; }
        50% { font-family: "Wingdings", "Webdings", sans-serif; }
        100% { font-family: "Comic Sans MS", cursive, sans-serif; }
      }
      
      /* Random CSS mutations */
      .MuiPaper-root:hover {
        transform: skew(2deg, 1deg);
        background: linear-gradient(45deg, rgba(255,0,255,0.2), rgba(0,255,255,0.2)) !important;
        border: 2px dashed #ff00ff !important;
      }
      
      /* Input fields go wild */
      input:focus, .MuiInputBase-input:focus {
        animation: shake 0.5s infinite;
        background-color: rgba(255, 0, 255, 0.1) !important;
      }
      
      @keyframes shake {
        0% { transform: translateX(0); }
        25% { transform: translateX(-5px); }
        50% { transform: translateX(5px); }
        75% { transform: translateX(-5px); }
        100% { transform: translateX(5px); }
      }
      
      /* Add a glitchy cursor */
      body.css-lab-leak-active {
        cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="%23ff00ff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z"></path><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path><path d="M2 2l7.586 7.586"></path><circle cx="11" cy="11" r="2"></circle></svg>'), auto;
      }
    `;
    document.head.appendChild(style);
    document.body.classList.add("css-lab-leak-active");

    // Create warning element
    const warning = document.createElement("div");
    warning.id = "css-lab-leak-warning";
    warning.innerHTML = `
      <div style="position: fixed; top: 20px; left: 20px; background-color: #ff0000; color: white; padding: 10px; border-radius: 5px; z-index: 10000; font-family: monospace; box-shadow: 0 0 20px rgba(255, 0, 0, 0.7); animation: pulse 1s infinite alternate;">
        <strong>WARNING: EXPERIMENTAL CSS ESCAPED CONTAINMENT</strong>
        <div style="font-size: 12px; margin-top: 5px;">Containment breach detected. Mutations spreading.</div>
      </div>
    `;
    document.body.appendChild(warning);

    // Add animation style
    const animStyle = document.createElement("style");
    animStyle.id = "css-lab-leak-animation";
    animStyle.innerHTML = `
      @keyframes pulse {
        0% { opacity: 0.7; transform: scale(0.98); }
        100% { opacity: 1; transform: scale(1); }
      }
    `;
    document.head.appendChild(animStyle);
  };

  // Remove all CSS Lab Leak effects
  const removeCSSLabLeakEffects = () => {
    // Remove style
    const style = document.getElementById("css-lab-leak-style");
    if (style) {
      document.head.removeChild(style);
    }

    // Remove animation style
    const animStyle = document.getElementById("css-lab-leak-animation");
    if (animStyle) {
      document.head.removeChild(animStyle);
    }

    // Remove warning
    const warning = document.getElementById("css-lab-leak-warning");
    if (warning) {
      document.body.removeChild(warning);
    }

    document.body.classList.remove("css-lab-leak-active");
  };

  return (
    <>
      {showWarning && (
        <Snackbar
          open={showWarning}
          autoHideDuration={5000}
          onClose={() => setShowWarning(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity="error"
            variant="filled"
            sx={{
              backgroundColor: "#ff0000",
              color: "white",
              animation: "pulse 1s infinite alternate",
              "@keyframes pulse": {
                "0%": { opacity: 0.7 },
                "100%": { opacity: 1 },
              },
            }}
          >
            {roleAssigned
              ? "CSS Lab Leak role assigned! Containment breach successful."
              : roleError
              ? `Role assignment failed: ${roleError}`
              : "WARNING: EXPERIMENTAL CSS ESCAPED CONTAINMENT"}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default CSSLabLeak;
